import type { PerformanceSource, TradeEntry } from '@/utils/performanceSource';

export interface EquityPoint {
  month: string;
  axisLabel: string;
  label: string;
  value: number;
  pnl: number;
  isMonthStart: boolean;
  /** Start of the week/month this point represents — used for range slicing. */
  ts: number;
  /** Cumulative position in the full series (1-based), independent of any view window. */
  seq: number;
}

export interface DrawdownPoint {
  month: string;
  axisLabel: string;
  label: string;
  value: number;
  peak: number;
  ts: number;
  seq: number;
}

const DAY = 86_400_000;
const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

function parseDate(input: string): number {
  const [year, month, day] = input.split('.').map(Number);
  return Date.UTC(year, month - 1, day);
}

/** Monday (00:00 UTC) of the week that contains the given timestamp. */
function mondayOf(timestamp: number): number {
  const weekday = new Date(timestamp).getUTCDay();
  const daysSinceMonday = (weekday + 6) % 7;
  return timestamp - daysSinceMonday * DAY;
}

const round2 = (value: number) => Math.round(value * 100) / 100;

/** Capital actually at work: deposit minus the withdrawal already taken. */
function capitalBaseOf(source: PerformanceSource): number {
  return round2(source.startingBalance - source.accountMeta.withdrawal);
}

/** Final balance, derived from the trade log instead of being hard-coded. */
function endingBalanceOf(source: PerformanceSource): number {
  return round2(
    capitalBaseOf(source) +
      (source.trades as TradeEntry[]).reduce((sum, [, pnl]) => sum + pnl, 0),
  );
}

/** Adjust the final point so the curve lands exactly on the statement balance. */
function reconcile(points: EquityPoint[], endingBalance: number): EquityPoint[] {
  if (points.length === 0) return points;
  const last = points[points.length - 1];
  if (Math.abs(last.value - endingBalance) > 0.005) {
    points[points.length - 1] = { ...last, value: round2(endingBalance) };
  }
  return points;
}

/**
 * Aggregate the closed trades into one balance point per calendar week
 * (Monday–Sunday), so the resulting curve shows the account's real
 * week-to-week movement instead of a smoothed month-end line.
 */
export function buildWeeklyEquity(source: PerformanceSource): EquityPoint[] {
  const capitalBase = capitalBaseOf(source);
  const endingBalance = endingBalanceOf(source);
  const pnlByWeek = new Map<number, number>();

  (source.trades as TradeEntry[]).forEach(([date, pnl]) => {
    const week = mondayOf(parseDate(date));
    pnlByWeek.set(week, (pnlByWeek.get(week) ?? 0) + pnl);
  });

  const weeks = [...pnlByWeek.keys()].sort((a, b) => a - b);
  if (weeks.length === 0) return [];

  const firstWeek = weeks[0];
  const lastWeek = weeks[weeks.length - 1];

  const points: EquityPoint[] = [];
  let balance = capitalBase;
  let previousMonth = -1;

  for (let week = firstWeek; week <= lastWeek; week += 7 * DAY) {
    const pnl = pnlByWeek.get(week) ?? 0;
    balance += pnl;

    const date = new Date(week);
    const month = date.getUTCMonth();
    const isMonthStart = month !== previousMonth;
    previousMonth = month;

    points.push({
      month: MONTHS[month],
      axisLabel: `${MONTHS[month]} '${String(date.getUTCFullYear()).slice(2)}`,
      label: `Week of ${MONTHS[month]} ${date.getUTCDate()}, ${date.getUTCFullYear()}`,
      value: round2(balance),
      pnl: round2(pnl),
      isMonthStart,
      ts: week,
      seq: points.length + 1,
    });
  }

  return reconcile(points, endingBalance);
}

/**
 * Fold the same closed trades into one month-end balance point each, giving a
 * cleaner view of the longer-term trend.
 */
export function buildMonthlyEquity(source: PerformanceSource): EquityPoint[] {
  const capitalBase = capitalBaseOf(source);
  const endingBalance = endingBalanceOf(source);
  const pnlByMonth = new Map<string, number>();

  (source.trades as TradeEntry[]).forEach(([date, pnl]) => {
    const [year, month] = date.split('.').map(Number);
    const key = `${year}-${String(month).padStart(2, '0')}`;
    pnlByMonth.set(key, (pnlByMonth.get(key) ?? 0) + pnl);
  });

  const keys = [...pnlByMonth.keys()].sort();
  const points: EquityPoint[] = [];
  let balance = capitalBase;

  keys.forEach((key) => {
    const [year, month] = key.split('-').map(Number);
    const pnl = pnlByMonth.get(key) ?? 0;
    balance += pnl;
    points.push({
      month: MONTHS[month - 1],
      axisLabel: `${MONTHS[month - 1]} '${String(year).slice(2)}`,
      label: `${MONTHS[month - 1]} ${year}`,
      value: round2(balance),
      pnl: round2(pnl),
      isMonthStart: true,
      ts: Date.UTC(year, month - 1, 1),
      seq: points.length + 1,
    });
  });

  return reconcile(points, endingBalance);
}

/**
 * Turn an equity series into a drawdown series: at every point report how far
 * the balance sits below the highest balance seen so far (0% at a new high,
 * negative below it).
 */
export function computeDrawdown(points: EquityPoint[]): DrawdownPoint[] {
  let peak = -Infinity;

  return points.map((point) => {
    peak = Math.max(peak, point.value);
    const value = peak > 0 ? round2((point.value / peak - 1) * 100) : 0;
    return {
      month: point.month,
      axisLabel: point.axisLabel,
      label: point.label,
      value,
      peak: round2(peak),
      ts: point.ts,
      seq: point.seq,
    };
  });
}

export type RangeWindow = 'all' | '12m';

const RANGE_DAYS = 365;

/**
 * Keep either the full series or just the trailing 12 months of it. The chart
 * always builds from the complete trade log, so this only decides how much of
 * that history is drawn — the older points are never deleted, just hidden.
 * Falls back to the full series if the window would leave fewer than 2 points.
 */
export function sliceByRange<T extends { ts: number }>(
  points: T[],
  range: RangeWindow,
): T[] {
  if (range === 'all' || points.length === 0) return points;
  const lastTs = points[points.length - 1].ts;
  const cutoff = lastTs - RANGE_DAYS * DAY;
  const windowed = points.filter((point) => point.ts >= cutoff);
  return windowed.length >= 2 ? windowed : points;
}